import React, { useState, useEffect } from 'react';
import { StatusBar } from 'expo-status-bar';
import { View, Text, TouchableOpacity, StyleSheet, Alert, DeviceEventEmitter } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, spacing } from '../theme';
import { useI18n } from '../i18n';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getTier, TIERS } from '../services/PremiumService';
import UpgradeModal from '../components/UpgradeModal';
import { purchaseSubscription } from '../services/IAPService';
import { TABS, TabIcon } from './tabConfig';
import { useResponsive } from '../hooks/useResponsive';

import HomeScreen           from '../screens/HomeScreen';
import LooksScreen          from '../screens/LooksScreen';
import CatalogScreen        from '../screens/CatalogScreen';
import WardrobeScreen       from '../screens/WardrobeScreen';
import ProfileScreen        from '../screens/ProfileScreen';
import TravelWardrobeScreen from '../screens/TravelWardrobeScreen';
import FavoritesScreen      from '../screens/FavoritesScreen';
import RequireAuth          from '../components/RequireAuth';

const Tab = createBottomTabNavigator();
// v1.12.117 — Icon + TABS extraits dans navigation/tabConfig.js (réutilisés
// par WebSidebar). Ici on wrap juste TabIcon pour fournir les couleurs theme.
function useTabColors() {
  const { theme } = useTheme();
  return { ON: theme.tabActive || colors.text.primary, OFF: theme.tabInactive || '#C8C0B4' };
}

function Icon({ name, active }) {
  const { ON, OFF } = useTabColors();
  return <TabIcon name={name} active={active} colorOn={ON} colorOff={OFF} />;
}

// ═══════════════════════════════════════════════════════════════
// v1.12.117 — Wrappers d'auth gating pour le browse libre web.
// Sur web, les onglets Looks / Dressing / Voyage / Favoris / Profil
// nécessitent une authentification. RequireAuth affiche un écran
// "Connecte-toi" si user absent, sinon renvoie l'écran réel.
// Sur natif, isAuthenticated est garanti au cold start → passthrough.
// ═══════════════════════════════════════════════════════════════
function LooksGuardedScreen(props) {
  return <RequireAuth feature="looks"><LooksScreen {...props} /></RequireAuth>;
}
function WardrobeGuardedScreen(props) {
  return <RequireAuth feature="wardrobe"><WardrobeScreen {...props} /></RequireAuth>;
}
function FavoritesGuardedScreen(props) {
  // v1.12.275 — i18n via feature (RequireAuth pioche la bonne clé)
  return <RequireAuth feature="favorites"><FavoritesScreen {...props} /></RequireAuth>;
}
function ProfileGuardedScreen(props) {
  return <RequireAuth feature="profile"><ProfileScreen {...props} /></RequireAuth>;
}

// ═══════════════════════════════════════════════════════════════
// Wrapper Voyage : bloque l'accès si le user n'a pas le tier qui débloque les trips
// (Basic / Premium / Gold → accès ; Free → bloqué)
// ═══════════════════════════════════════════════════════════════
function TravelGuardedScreen(props) {
  const { user, updateUserProfile } = useAuth();
  const { dark, theme } = useTheme();
  const { lang } = useI18n();
  const isFR = !lang || lang.startsWith('fr');
  const [allowed, setAllowed]     = useState(null);
  const [userTier, setUserTier]   = useState(null);
  // ═══ Modal paywall : UpgradeModal partagé avec les autres écrans (Looks/IA) ═══
  // Quand le user clique "CHOISIR UN ABONNEMENT" sur le paywall Voyage,
  // on ouvre le modal premium avec feature="travel" → liste des plans qui
  // débloquent le mode Voyage (Basic/Premium/Gold avec toggle mensuel/annuel).
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const tier = await getTier(user);
        setUserTier(tier);
        setAllowed(TIERS[tier]?.trips_access === true);
      } catch {
        setAllowed(false);
      }
    })();
  }, [user?.id, user?.tier, user?.plan]);

  // v1.12.650 — Force close au retour de Stripe (sinon UpgradeModal reste
  // visible et le click "Accueil" traverse vers un tier → re-checkout)
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('__stripe_force_close_paywalls__', () => {
      console.log('[TravelPaywall] 🧹 force close (post-Stripe)');
      setShowUpgrade(false);
    });
    return () => sub.remove();
  }, []);

  // Loading
  if (allowed === null) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.bg }}>
        <Text style={{ fontSize: 14, color: theme.textFaint }}>{isFR ? 'Chargement…' : 'Loading…'}</Text>
      </View>
    );
  }

  // Accès refusé → paywall élégant
  if (!allowed) {
    const features = isFR ? [
      '🎒 Planifier valise par voyage',
      '👗 Générer des looks avec vos articles emportés',
      '🔥 Filtrer par occasion (Casual, Bureau, Soirée…)',
      '🧳 Checklist catégorisée par type de voyage',
      '💡 Bons plans IA personnalisés par destination',
      '🛍️ Suggestions shopping pour ce voyage',
    ] : [
      '🎒 Plan a suitcase for each trip',
      '👗 Generate looks with the items you pack',
      '🔥 Filter by occasion (Casual, Office, Evening…)',
      '🧳 Checklist sorted by travel type',
      '💡 AI travel deals personalized by destination',
      '🛍️ Shopping suggestions for this trip',
    ];
    return (
      <>
        <View style={{ flex: 1, backgroundColor: theme.bg, padding: 24, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ fontSize: 64, marginBottom: 20 }}>✈️</Text>
          <Text style={{ fontSize: 22, fontWeight: '700', color: theme.text, letterSpacing: 0.3, marginBottom: 10, textAlign: 'center' }}>
            {isFR ? 'Mode Voyage' : 'Travel Mode'}
          </Text>
          <Text style={{ fontSize: 13, color: theme.textSec, textAlign: 'center', lineHeight: 20, marginBottom: 24, maxWidth: 320 }}>
            {isFR
              ? 'Planifiez vos tenues pour chaque voyage, recevez des bons plans IA personnalisés et ne ratez plus rien dans votre valise.'
              : 'Plan your outfits for every trip, get AI-personalized travel deals and never forget anything in your suitcase.'}
          </Text>

          <View style={{ backgroundColor: theme.bgSurface, borderRadius: 16, padding: 20, marginBottom: 24, width: '100%', maxWidth: 380, borderWidth: 1, borderColor: theme.bgBorder }}>
            <Text style={{ fontSize: 11, color: '#8B5CF6', letterSpacing: 1.5, fontWeight: '700', marginBottom: 14 }}>
              {isFR ? '🔒 DÉBLOQUÉ AVEC BASIC, PREMIUM OU GOLD' : '🔒 UNLOCKED WITH BASIC, PREMIUM OR GOLD'}
            </Text>
            {features.map((f, i) => (
              <Text key={i} style={{ fontSize: 13, color: theme.text, marginBottom: 8, lineHeight: 20 }}>
                {f}
              </Text>
            ))}
          </View>

          <TouchableOpacity
            style={{ backgroundColor: theme.btnPrimary || theme.text, paddingHorizontal: 32, paddingVertical: 14, borderRadius: 100, width: '100%', maxWidth: 380, alignItems: 'center' }}
            onPress={() => setShowUpgrade(true)}
          >
            <Text style={{ color: theme.btnPrimaryText || theme.bg, fontSize: 13, fontWeight: '700', letterSpacing: 1 }}>
              {isFR ? 'CHOISIR UN ABONNEMENT' : 'CHOOSE A PLAN'}
            </Text>
          </TouchableOpacity>

          <Text style={{ fontSize: 10, color: theme.textFaint, marginTop: 14, textAlign: 'center' }}>
            {isFR ? 'À partir de 4,99 €/mois · Annulable à tout moment' : 'From €4.99/month · Cancel anytime'}
          </Text>
        </View>

        {/* ═══ Modal de sélection de plan (RevenueCat) ═══ */}
        {/* Affiche les 3 plans qui débloquent Voyage : Basic / Premium / Gold
            avec toggle mensuel/annuel, comme sur l'écran Looks ou Catalogue.
            Sur achat → RevenueCat → webhook /webhook/revenuecat → DB tier mis à jour
            → useEffect ci-dessus re-check getTier → allowed devient true → vrai écran. */}
        <UpgradeModal
          visible={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          currentTier={userTier || 'free'}
          feature="travel"
          onSubscribe={async (planId) => {
            try {
              const tier = planId.replace('_yearly', '');
              const period = planId.includes('_yearly') ? 'yearly' : 'monthly';
              const result = await purchaseSubscription(tier, period, user);
              if (result?.success) {
                // Refresh tier depuis cache local — la DB sera updatée par le webhook RC
                const newTier = await getTier(user);
                setUserTier(newTier);
                setAllowed(TIERS[newTier]?.trips_access === true);
                if (updateUserProfile) updateUserProfile({ tier: newTier });
                setShowUpgrade(false);
              }
            } catch (e) {
              console.warn('[TravelPaywall] purchase error:', e?.message);
              Alert.alert('Erreur', 'Impossible de finaliser l\'achat : ' + (e?.message || 'erreur inconnue'));
            }
          }}
        />
      </>
    );
  }

  // Accès autorisé → afficher le vrai écran
  return <TravelWardrobeScreen {...props} />;
}

function OutfitTabBar({ state, navigation }) {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const { dark, theme } = useTheme();
  const { isDesktopWeb } = useResponsive();

  // v1.12.117 — sur desktop web la nav est gérée par <WebSidebar /> (App.js).
  // On retourne null pour cacher complètement la TabBar bottom.
  if (isDesktopWeb) return null;

  const tabs = TABS;
  const currentRouteName = state.routes[state.index]?.name;

  // v1.12.220+221 — Quand l'utilisateur quitte un onglet "à filtres" (Catalogue,
  // Garde-robe, Looks/Suggestions, Voyage) vers un autre onglet, on émet un
  // event global pour que TOUS les écrans concernés remettent leur état
  // (scroll, recherche, filtres, modals) à leur position par défaut, comme s'il
  // y entrait pour la première fois.
  const FILTERED_TABS = ['Catalogue', 'Garde-robe', 'Suggestions', 'Voyage'];
  const handleTabPress = React.useCallback((tabKey) => {
    if (FILTERED_TABS.includes(currentRouteName) && tabKey !== currentRouteName) {
      try {
        const { DeviceEventEmitter } = require('react-native');
        DeviceEventEmitter.emit('tabs_reset_to_default', { from: currentRouteName, to: tabKey });
      } catch {}
    }
    navigation.navigate(tabKey);
  }, [currentRouteName, navigation]);

  // v1.12.71 — paddingBottom = insets.bottom pour remonter les icônes au-dessus
  // du home indicator iPhone. paddingTop: 0 (pas d'espace blanc au-dessus).
  return (
    <View style={[s.bar, { paddingBottom: insets.bottom || 8, backgroundColor: theme.tabBg }]}>
      {tabs.map((tab, index) => {
        const focused = currentRouteName === tab.key;
        return (
          <TouchableOpacity key={tab.key} style={s.tab}
            onPress={() => handleTabPress(tab.key)} activeOpacity={0.6}>
            <View style={s.iconWrap}>
              <Icon name={tab.icon} active={focused} />
              {tab.badge > 0 && (
                <View style={s.badge}>
                  <Text style={s.badgeText}>{tab.badge > 9 ? '9+' : tab.badge}</Text>
                </View>
              )}
            </View>
            <Text numberOfLines={1} style={[s.tabLabel, { color: focused ? (theme.tabActive||colors.text.primary) : (theme.tabInactive||'#C8C0B4') }]}>{tab.labelKey ? t(tab.labelKey) : tab.label}</Text>
            {focused && <View style={[s.activeBar, { backgroundColor: theme.tabActive || colors.text.primary }]} />}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

export default function TabNavigator() {
  const { dark, theme } = useTheme();
  return (
    <>
      <StatusBar style={dark ? 'light' : 'dark'} />
      <Tab.Navigator tabBar={props => <OutfitTabBar {...props} />}
        screenOptions={{
          headerShown: false,
          // v1.12.898 — Perf Android : lazy + freezeOnBlur
          // lazy: les onglets non visités ne sont PAS montés au démarrage
          // freezeOnBlur: les onglets non focus sont "pausés" (pas de re-render,
          //   animations gelées, timers stoppés) → décharge le main thread.
          lazy: true,
          freezeOnBlur: true,
        }}>
        {/* ═══ 7 onglets : Accueil → Catalogue → Looks → Dressing → Valise → Favoris → Profil ═══ */}
        {/* Home et Catalogue accessibles sans auth (browse libre web).
            Looks/Dressing/Voyage/Favoris/Profil → RequireAuth si !user. */}
        <Tab.Screen name="Home"        component={HomeScreen}              />
        <Tab.Screen name="Catalogue"   component={CatalogScreen}           />
        <Tab.Screen name="Suggestions" component={LooksGuardedScreen}      />
        <Tab.Screen name="Garde-robe"  component={WardrobeGuardedScreen}   />
        {/* v1.12.911 — Onglet Voyage déjà absent de TABS (tabConfig.js v1.12.238).
            Screen reste enregistré pour les deep-links et navigation conditionnelle. */}
        <Tab.Screen name="Voyage"      component={TravelGuardedScreen}     />
        <Tab.Screen name="Favoris"     component={FavoritesGuardedScreen}  />
        <Tab.Screen name="Profil"      component={ProfileGuardedScreen}    />
      </Tab.Navigator>
    </>
  );
}

// v1.12.71 — Style Facebook iOS : icônes 24px, label 10px, pas d'espace blanc
// au-dessus des icônes (paddingTop: 0). Le safe area bottom est ajouté dynamiquement.
const s = StyleSheet.create({
  bar:           { flexDirection: 'row', backgroundColor: '#fff', paddingTop: 0 },
  tab:           { flex: 1, alignItems: 'center', position: 'relative', paddingBottom: 4, paddingHorizontal: 2 },
  iconWrap:      { position: 'relative', marginBottom: 3 },
  badge:         { position: 'absolute', top: -3, right: -6, width: 16, height: 16, borderRadius: 8, backgroundColor: colors.status.error, alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: '#fff' },
  badgeText:     { fontSize: 8, color: '#fff', fontWeight: '700' },
  tabLabel:      { fontSize: 10, letterSpacing: 0.2, color: '#C8C0B4' },
  tabLabelActive:{ color: colors.text.primary, fontWeight: '500' },
  activeBar:     { position: 'absolute', top: 0, width: 24, height: 2, backgroundColor: colors.text.primary, borderRadius: 1 },
});
